import React from 'react';

import classnames from 'classnames';

import {BulletedList} from '../../bulleted-list/bulleted-list';
import {Button} from '../../button/button';
import {ButtonCard} from '../../card/button-card';
import {colors} from '../../colors/colors';
import {WarningIcon} from '../../icon/svg/warning-icon';
import {Body} from '../../typography/body';

import {getIcon} from '../helpers';
import {PlanPrice} from '../plan-price';
import type {Plan} from '../types';

import {PlanPickerCardInfoBadge} from './plan-picker-card-info-badge';
import {PlanPickerCardSelectionBadge} from './plan-picker-card-selection-badge';

import styles from './plan-picker-card.module.css';

type Props = {
    plan: Plan;
    isSelected: boolean;
    onIsSelectedChange: (selectedPlanCode: string) => void;
    variant?: 'default' | 'engagement' | 'marketing';
};

export function PlanPickerCard(props: Props) {
    const bullets = props.plan.planHighlights.map((highlight) => (
        <Body size='small' key={highlight}>
            {highlight}
        </Body>
    ));
    const cardRef = React.useRef();

    const buttonText = getButtonText(props.isSelected, props.plan.isActive);
    const isLegacy = props.plan.isLegacy;
    const descriptionClassNames = classnames({
        'flex-grow flex flex-dir-col gap-8': true,
        'py-24': !isLegacy,
        'pb-24': isLegacy,
    });

    return (
        <div
            style={{
                // @ts-expect-error
                width: (props.variant === 'engagement' || props.variant === 'marketing') && '230px',
            }}
            // @ts-expect-error
            ref={cardRef}
            className={classnames('full-width pos-relative', styles.container)}
        >
            <PlanPickerCardSelectionBadge
                isSelected={props.isSelected}
                isActive={props.plan.isActive}
            />
            <ButtonCard
                onClick={() => {
                    props.onIsSelectedChange(props.plan.code);
                }}
                isSelected={props.isSelected}
                isFullWidth={true}
            >
                <div
                    className={classnames(
                        'full-height border-box grid gap-8 text-align-left pos-relative',
                        styles.content
                    )}
                >
                    <PlanPickerCardInfoBadge plan={props.plan} />
                    <div className='flex justify-center'>{getIcon(props.plan.code)}</div>
                    <div className='py-16 full-height border-box flex flex-dir-col justify-end'>
                        <div className={descriptionClassNames}>
                            {isLegacy && (
                                <div className='flex justify-center'>
                                    {/* @ts-expect-error */}
                                    <WarningIcon size={20} color={colors.yellow} />
                                </div>
                            )}
                            <Body size='small' color={isLegacy ? 'light' : 'medium'}>
                                {props.plan.description}
                            </Body>
                        </div>
                        <PlanPrice
                            price={props.plan.price}
                            stickerPrice={props.plan.stickerPrice}
                            justify='start'
                        />
                        <Body>{props.plan.priceUnit}</Body>
                    </div>
                    <Button as='div' variant='primary' disabled={props.isSelected} size='big'>
                        {buttonText}
                    </Button>
                    <div className='py-8 flex flex-dir-col gap-8'>
                        {props.plan.priorFeaturesText && (
                            <Body color='light' size='small'>
                                {props.plan.priorFeaturesText}
                            </Body>
                        )}
                        <BulletedList bullets={bullets} size='small' />
                    </div>
                </div>
            </ButtonCard>
        </div>
    );
}

function getButtonText(isSelected: boolean, isActive: boolean) {
    if (isActive && isSelected) {
        return 'Current plan';
    }

    if (isActive && !isSelected) {
        return 'Re-select this plan';
    }

    if (!isActive && isSelected) {
        return 'Selected plan';
    }

    return 'Choose plan';
}
